// @todo: Migrate to 'DropdownMenu' from @wandb/components. For more details see:
// https://wandb.github.io/components/?path=/docs/migration-guide--docs
// eslint-disable-next-line wandb/no-deprecated-imports
import ModifiedDropdown from '@wandb/weave/common/components/elements/ModifiedDropdown';
// @todo: Migrate to 'Switch' from @wandb/components. For more details see:
// https://wandb.github.io/components/?path=/docs/migration-guide--docs
// eslint-disable-next-line wandb/no-deprecated-imports
import {Switch} from '@wandb/weave/components';
// @todo: Migrate to @wandb/components. For more details see:
// https://wandb.github.io/components/?path=/docs/migration-guide--docs
import {Tailwind, TailwindContents} from '@wandb/weave/components/Tailwind';
// @todo: Migrate to 'Tooltip' from @wandb/components. For more details see:
// https://wandb.github.io/components/?path=/docs/migration-guide--docs
// eslint-disable-next-line wandb/no-deprecated-imports
import {Tooltip} from '@wandb/weave/components/Tooltip';
import React, {useContext, useMemo, useRef} from 'react';
// eslint-disable-next-line wandb/no-deprecated-imports
import {Tab} from 'semantic-ui-react';
import {twMerge} from 'tailwind-merge';

import {SmoothingType, XAxisFormatValues} from '../../../generated/graphql';
import {SettingType} from '../../../services/analytics/workspaceSettingsEvents';
import {RunQueryContext} from '../../../state/runs/context';
import * as QueryTypes from '../../../state/runs/types';
import {Line, prettyXAxisLabel} from '../../../util/plotHelpers';
import {
  useRampFlagFullFidelityRunsPerChart,
  useStatsigGateWorkspaceConsolidation,
} from '../../../util/rampFeatureFlags';
import * as RunHelpers from '../../../util/runhelpers';
import {MetricsPicker} from '../../elements/MetricsPicker';
import RangeInput from '../../elements/RangeInput';
import {useKeyInfoQueryContext} from '../../MultiRunWorkspace/KeyInfoQueryContext';
import {useSingleRunModeContext} from '../../MultiRunWorkspace/SingleRunMode';
import {usePanelActionsContext} from '../../Panel/PanelEditor/PanelEditor';
import {DebouncedInputOption} from '../../PanelChartOptions';
import {ResetSettingButton} from '../../WorkspaceDrawer/Settings/components/ResetSettingButton';
import {SettingIndentedRow} from '../../WorkspaceDrawer/Settings/components/SettingIndentedRow';
import {SettingLabel} from '../../WorkspaceDrawer/Settings/components/SettingLabel';
import {useTrackSettingsChange} from '../../WorkspaceDrawer/Settings/hooks/useTrackSettingEvent';
import {LinePlotConfigExcludeOutliers} from '../../WorkspaceDrawer/Settings/runLinePlots/LinePlotConfigExcludeOutliers';
import {LinePlotPointAggregation} from '../../WorkspaceDrawer/Settings/runLinePlots/LinePlotPointAggregation';
import {
  EMPTY_SMOOTHING_SETTINGS,
  LinePlotSmoothing,
} from '../../WorkspaceDrawer/Settings/runLinePlots/LinePlotSmoothing';
import {getMaxRunLimitWithOverrideFlag} from '../../WorkspaceDrawer/Settings/runLinePlots/utils/getCascadingMaxRuns';
import {
  XAxisFormat,
  xAxisIsBuiltin,
} from '../../WorkspaceDrawer/Settings/runLinePlots/XAxisFormat';
import {
  DerivedPointVisualizationOption,
  isPointVizFullFidelity,
} from '../../WorkspaceDrawer/Settings/types';
import {defaultTitle} from '../common';
import {X_AXIS_LABELS} from '../defaults';
import {PlotTypeSelection} from '../edit/PlotTypeSelection';
import {usePanelConfigContext} from '../PanelConfigContext';
import {usePanelInteractionContext} from '../PanelInteractionContext';
import {usePanelZoom} from '../PanelZoomContext';
import {useProjectIsV2} from '../ProjectDetails';
import {usePanelGroupingSettings} from '../RunsLinePlotContext/usePanelGroupingSettings';
import {RunsLinePlotConfig} from '../types';
import {getXAxisChoices, getYAxisChoices} from '../utils/axis';
import {isBarPlot, isEmptyChart} from '../utils/checkTypes';
import {usePanelZoom2} from '../zoom/PanelZoomContext2';
import {EditMaxRuns} from './EditMaxRuns';

type EditDataTabProps = {
  updateConfig: (config: RunsLinePlotConfig) => void;
  config: RunsLinePlotConfig;
  query: QueryTypes.QueryConfig;
  lines: Line[];
};

export const EditDataTab = ({
  updateConfig,
  config,
  query,
  lines,
}: EditDataTabProps) => {
  const {
    excludeOutliers,
    pointVisualizationMethod,
    smoothingType,
    smoothingWeight,
    xAxis,
    xAxisMax,
    xAxisMin,
    xLogScale,
    xAxisFormat,
    yLogScale,
    showOriginalAfterSmoothing,
  } = usePanelConfigContext();
  const {isSingleRun} = useSingleRunModeContext();
  const isBucketing = isPointVizFullFidelity(pointVisualizationMethod);
  const canOverrideMaxRuns = useRampFlagFullFidelityRunsPerChart();

  const {handleConfigZoomChange, handleUserZoomChange} = usePanelZoom();
  const {zooming: zooming1} = usePanelInteractionContext();
  const {handleUserZoom, configZoom, zooming: zooming2} = usePanelZoom2();

  const trackPanelSettingsChange = useTrackSettingsChange();
  const {setRunOnApply} = usePanelActionsContext();

  const isWorkspaceConsolidationEnabled =
    useStatsigGateWorkspaceConsolidation();
  const isOldSingleRunWorkspace =
    isSingleRun && !isWorkspaceConsolidationEnabled;

  /**
   * Conditional fns based on what kind of plot we're in. We're moving bucketing on to the new zoom context and we plan on deprecating the old one, but for now we're going to have to support both zoom contexts
   * - Bucketing: update the configZoom object directly
   * - Non-bucketing: use the handler from the context
   */
  const updateUserZoom = isBucketing ? handleUserZoomChange : handleUserZoom;
  const updateConfigZoom = isBucketing ? updateConfig : handleConfigZoomChange;
  const zooming = isBucketing ? zooming2 : zooming1;
  const renderingBarPlot = isBarPlot(lines, zooming);

  const {fromV2} = useProjectIsV2({
    entityName: query.entityName,
    projectName: query.projectName,
  });
  const runsContext = useContext(RunQueryContext);
  const settingsToTrack = useRef<Record<string, any>>({});

  const {
    keyInfoQuery: {historyKeyInfo: keyInfo},
  } = useKeyInfoQueryContext();

  const keyTypes = useMemo(
    function makeKeyTypes() {
      return RunHelpers.keyTypes(keyInfo.keys);
    },
    [keyInfo]
  );

  const [xAxisChoices, yAxisChoices] = useMemo(
    function memoXAndYChoices() {
      return [
        getXAxisChoices({
          configMetrics: config.metrics ?? [],
          configXAxis: xAxis,
          keyInfo,
          keyTypes,
        }),
        getYAxisChoices({
          configMetrics: config.metrics ?? [],
          configXAxis: xAxis,
          keyInfo,
          keyTypes,
          isHistogramAllowed: isSingleRun || runsContext.report != null,
        }),
      ];
    },
    [keyInfo, config.metrics, xAxis, keyTypes, isSingleRun, runsContext]
  );

  const xAxisOptions = useMemo(() => {
    return xAxisChoices.map(k => {
      const notMonotonic =
        keyInfo.keys[k] != null && !keyInfo.keys[k].monotonic;

      let icon = 'arrow up';
      if (['_runtime', '_timestamp', '_absolute_runtime'].indexOf(k) > -1) {
        icon = 'calendar';
      } else if (notMonotonic) {
        icon = 'chart bar';
      }

      let text = X_AXIS_LABELS[k] || k;
      if (notMonotonic && !fromV2) {
        text += ' (Not Monotonically Increasing)';
      }

      return {
        icon,
        text,
        value: k,
        key: k,
      };
    });
  }, [keyInfo.keys, fromV2, xAxisChoices]);

  const {isAnyRunsetGrouped: isGrouped} = usePanelGroupingSettings();

  const xAxisTitle = (
    <DebouncedInputOption
      value={config.xAxisTitle}
      update={xAxisTitle => updateConfig({xAxisTitle})}
      placeholder={prettyXAxisLabel(xAxis, lines)}
    />
  );
  const yAxisTitle = (
    <DebouncedInputOption
      value={config.yAxisTitle}
      update={yAxisTitle => updateConfig({yAxisTitle})}
    />
  );

  const xAxisRange = (
    <div>
      {!xAxisIsBuiltin(xAxis) ? (
        <Tailwind>
          <SettingIndentedRow className="ml-0">
            <div className="ml-12">
              <XAxisFormat
                trackSetting={() => {}}
                updateSetting={updateConfig}
                xAxisFormat={xAxisFormat}
              />
              <div className="flex min-w-[360px] items-center">
                <SettingLabel text="Range" className="ml-14 w-[87px]" />
                <RangeInput
                  disabled={
                    isEmptyChart(config) ||
                    (renderingBarPlot && xAxisMin == null && xAxisMax == null)
                  }
                  label="X Axis"
                  onMinChange={newVal => {
                    if (
                      (configZoom.xAxisMax ?? Infinity) < (newVal ?? -Infinity)
                    ) {
                      return;
                    }
                    if (isBucketing) {
                      updateConfig({
                        xAxisMin: newVal,
                      });
                    } else {
                      handleConfigZoomChange({
                        xAxisMin: newVal,
                      });
                    }
                  }}
                  onMaxChange={newVal => {
                    if (
                      (configZoom.xAxisMin ?? -Infinity) > (newVal ?? Infinity)
                    ) {
                      return;
                    }
                    if (isBucketing) {
                      updateConfig({
                        xAxisMax: newVal,
                      });
                    } else {
                      handleConfigZoomChange({
                        xAxisMax: newVal,
                      });
                    }
                  }}
                  minValue={configZoom.xAxisMin}
                  maxValue={configZoom.xAxisMax}
                  log={true}
                  logValue={xLogScale}
                  onLogChange={newVal => {
                    updateConfig({
                      xLogScale: newVal,
                    });
                  }}
                />
              </div>
            </div>
          </SettingIndentedRow>
        </Tailwind>
      ) : (
        <RangeInput
          disabled={
            isEmptyChart(config) ||
            (renderingBarPlot && xAxisMin == null && xAxisMax == null)
          }
          label="X Axis"
          onMinChange={newVal => {
            if ((configZoom.xAxisMax ?? Infinity) < (newVal ?? -Infinity)) {
              return;
            }
            if (isBucketing) {
              updateConfig({
                xAxisMin: newVal,
              });
            } else {
              handleConfigZoomChange({
                xAxisMin: newVal,
              });
            }
          }}
          onMaxChange={newVal => {
            if ((configZoom.xAxisMin ?? -Infinity) > (newVal ?? Infinity)) {
              return;
            }
            if (isBucketing) {
              updateConfig({
                xAxisMax: newVal,
              });
            } else {
              handleConfigZoomChange({
                xAxisMax: newVal,
              });
            }
          }}
          minValue={configZoom.xAxisMin}
          maxValue={configZoom.xAxisMax}
          log={true}
          logValue={xLogScale}
          onLogChange={newVal => {
            updateConfig({
              xLogScale: newVal,
            });
          }}
        />
      )}
    </div>
  );

  const yAxisRange = (
    <RangeInput
      disabled={isEmptyChart(config)}
      onMinChange={newVal => {
        if ((configZoom.yAxisMax ?? Infinity) < (newVal ?? -Infinity)) {
          return;
        }
        if (isBucketing) {
          updateConfig({yAxisMin: newVal});
        } else {
          handleConfigZoomChange({yAxisMin: newVal});
        }
      }}
      onMaxChange={newVal => {
        if ((configZoom.yAxisMin ?? -Infinity) > (newVal ?? Infinity)) {
          return;
        }
        if (isBucketing) {
          updateConfig({yAxisMax: newVal});
        } else {
          handleConfigZoomChange({yAxisMax: newVal});
        }
      }}
      minValue={configZoom.yAxisMin}
      maxValue={configZoom.yAxisMax}
      label="Y Axis"
      log
      logValue={yLogScale}
      onLogChange={newVal =>
        updateConfig({
          yLogScale: newVal,
        })
      }
    />
  );

  return (
    <Tab.Pane as="div" className="form-grid" data-test="edit-data-tab">
      <Tailwind>
        <div
          className={twMerge('flex flex-col', 'gap-20 pb-16', 'h-full w-full')}>
          <div
            className={twMerge('flex flex-col', 'gap-8')}
            data-test="x-axis-setting">
            <div className="flex w-full items-center">
              <Tooltip
                trigger={<SettingLabel text="X axis" />}
                content="Use step, time or any variable logged with wandb.log as xaxis."
              />
              <div
                data-test="x-axis-select"
                className="item-center flex w-full flex-row gap-8">
                <ModifiedDropdown
                  allowRegexSearch
                  lazyLoad
                  options={xAxisOptions}
                  placeholder="X Axis"
                  search
                  selection
                  style={{flexGrow: 1}}
                  value={xAxis}
                  onChange={(e, {value}) => {
                    const newConfigZoom: Partial<RunsLinePlotConfig> = {
                      xAxisMin: undefined,
                      xAxisMax: undefined,
                    };
                    const xAxisFormat =
                      value !== config.xAxis && value === '_timestamp'
                        ? XAxisFormatValues.Datetime
                        : XAxisFormatValues.Number;
                    if (!isBucketing) {
                      updateConfig({
                        xAxis: value as string,
                        xAxisFormat,
                      });
                    } else {
                      newConfigZoom.xAxis = value as string;
                      newConfigZoom.xAxisFormat = xAxisFormat;
                    }
                    updateConfigZoom(newConfigZoom);
                    updateUserZoom({
                      xAxisMin: undefined,
                      xAxisMax: undefined,
                    });
                  }}
                />
                <ResetSettingButton
                  isEnabled
                  onButtonClick={() => {
                    updateConfig({
                      xAxis: undefined,
                      xAxisMin: undefined,
                      xAxisMax: undefined,
                      xLogScale: undefined,
                      xAxisTitle: undefined,
                      xAxisFormat: undefined,
                    });
                  }}
                  settingLevel="panel"
                />
              </div>
            </div>
            <div className="flex">
              <SettingIndentedRow>
                <div className="flex flex-col gap-8">
                  <div className="flex items-center">
                    <Tooltip
                      trigger={
                        <SettingLabel text="Range" className="ml-14 w-[87px]" />
                      }
                      content="Fix the minimum and maximum values for the x-axis"
                    />
                    {xAxisRange}
                  </div>
                  <div className="flex items-center">
                    <Tooltip
                      trigger={
                        <SettingLabel text="Label" className="ml-14 w-[87px]" />
                      }
                      content="Set the title for the x-axis"
                    />
                    <div className="flex">{xAxisTitle}</div>
                  </div>
                </div>
              </SettingIndentedRow>
            </div>
          </div>
        </div>
      </Tailwind>

      <Tailwind>
        <div
          className={twMerge('flex flex-col', 'gap-20 py-16', 'h-full w-full')}>
          <div className={twMerge('flex flex-col', 'gap-8')}>
            <div className="flex w-full items-center">
              <Tooltip
                trigger={<SettingLabel text="Y axis" />}
                content="Plot any numeric metric logged with wandb.log."
              />
              <div className="item-center flex w-full flex-row gap-8">
                <MetricsPicker
                  config={config}
                  keyInfo={keyInfo}
                  keyTypes={keyTypes}
                  options={yAxisChoices}
                  updateConfig={updateConfig}
                />
              </div>
            </div>
            <div className="flex">
              <SettingIndentedRow>
                <div className="flex flex-col gap-8">
                  <div className="flex items-center">
                    <Tooltip
                      trigger={
                        <SettingLabel text="Range" className="ml-14 w-[87px]" />
                      }
                      content="Fix the minimum and maximum values for the y-axis"
                    />
                    {yAxisRange}
                  </div>
                  <div className="flex items-center">
                    <Tooltip
                      trigger={
                        <SettingLabel text="Label" className="ml-14 w-[87px]" />
                      }
                      content="Set the title for the y-axis"
                    />
                    <div className="flex">{yAxisTitle}</div>
                  </div>
                </div>
              </SettingIndentedRow>
            </div>
            <div className="mt-8 flex items-center">
              <SettingLabel text="Chart title" />
              <DebouncedInputOption
                value={config.chartTitle}
                update={chartTitle => updateConfig({chartTitle})}
                placeholder={defaultTitle(config)}
              />
            </div>
          </div>
        </div>
      </Tailwind>

      {!isOldSingleRunWorkspace && (
        <EditMaxRuns
          config={config}
          isBucketing={isBucketing}
          isGrouped={isGrouped}
          settingsToTrack={settingsToTrack.current}
          setRunOnApply={setRunOnApply}
          trackPanelSettingsChange={trackPanelSettingsChange}
          updateConfig={updateConfig}
        />
      )}

      {isPointVizFullFidelity(pointVisualizationMethod) && (
        <TailwindContents>
          <LinePlotConfigExcludeOutliers
            excludeOutliers={excludeOutliers ?? 'include-outliers'}
            onExcludeOutliersChange={(areOutliersExcluded: boolean) => {
              settingsToTrack.current = {
                ...settingsToTrack.current,
                excludeOutliers: areOutliersExcluded
                  ? 'exclude-outliers'
                  : 'include-outliers',
              };

              setRunOnApply(() => () => {
                trackPanelSettingsChange({
                  setting: 'excludeOutliers',
                  settingValue: settingsToTrack.current?.excludeOutliers,
                });
              });
              updateConfig({
                excludeOutliers: areOutliersExcluded
                  ? 'exclude-outliers'
                  : 'include-outliers',
                // @ts-expect-error - ignoreOutliers is deprecated
                ignoreOutliers: undefined,
                showMinMaxOnHover: undefined,
              });
            }}
          />
        </TailwindContents>
      )}

      <TailwindContents>
        <LinePlotPointAggregation
          className={twMerge('group mt-12')}
          clearSetting={() => {
            updateConfig({
              pointVisualizationMethod: undefined,
            });
          }}
          displayValues={{
            pointVisualizationMethod,
          }}
          onPointVisualizationMethodChange={(
            value: DerivedPointVisualizationOption
          ) => {
            settingsToTrack.current = {
              ...settingsToTrack.current,
              pointVisualizationMethod: value,
            };
            setRunOnApply(() => () => {
              trackPanelSettingsChange({
                setting: 'pointVisualizationMethod',
                settingValue:
                  settingsToTrack.current?.pointVisualizationMethod?.toString(),
              });
            });

            const newRunsLimit = getMaxRunLimitWithOverrideFlag({
              canOverrideMaxRuns,
              isBucketing: isPointVizFullFidelity(value),
            });

            updateConfig({
              pointVisualizationMethod: value,
              limit:
                config.limit != null && newRunsLimit
                  ? Math.min(config.limit, newRunsLimit)
                  : undefined, // don't need to set the limit if it wasn't already set
            });
          }}
          settingLevel="panel"
          values={{
            pointVisualizationMethod: config.pointVisualizationMethod,
          }}
        />
      </TailwindContents>

      <TailwindContents>
        <LinePlotSmoothing
          className={twMerge(
            'group my-12',
            smoothingType === SmoothingType.None ? 'mb-8' : ''
          )}
          displayValues={{
            smoothingWeight,
            smoothingType,
          }}
          settingLevel="panel"
          updateSetting={updateConfig}
          trackSetting={(setting: SettingType, value: string | undefined) => {
            trackPanelSettingsChange({
              setting,
              settingValue: value,
            });
          }}
          clearSetting={() => {
            updateConfig(EMPTY_SMOOTHING_SETTINGS);
          }}
          values={{
            smoothingWeight: config.smoothingWeight,
            smoothingType: config.smoothingType,
          }}
        />
      </TailwindContents>
      {smoothingWeight !== 0 && (isBucketing || !isGrouped) && (
        <Tailwind>
          <div className="mt-8 flex items-center">
            <Tooltip
              trigger={<SettingLabel text="Show Original" />}
              content="Show lines before smoothing"
            />
            <Switch.Root
              name="aggregate"
              size="medium"
              checked={showOriginalAfterSmoothing}
              onCheckedChange={checked => {
                updateConfig({
                  showOriginalAfterSmoothing: checked,
                });
                trackPanelSettingsChange({
                  setting: 'showOriginalAfterSmoothing',
                  settingValue: checked.toString(),
                });
              }}>
              <Switch.Thumb
                size="medium"
                checked={showOriginalAfterSmoothing}
              />
            </Switch.Root>
          </div>
        </Tailwind>
      )}

      {!renderingBarPlot &&
        (config.plotType != null ||
          !isOldSingleRunWorkspace ||
          (config.metrics != null && config.metrics.length > 1)) && (
          <div data-test="plot-type-selection">
            <PlotTypeSelection
              config={config as RunsLinePlotConfig}
              updateConfig={updateConfig}
            />
          </div>
        )}
    </Tab.Pane>
  );
};
